import React, { useState } from 'react'
import { Booklet } from '../../../../types/generated.types'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { statOrdersByAssociationQuery } from '../../../../queries/association'
import { getBookletCompaniesCountQuery } from '../../../../queries/booklet'
import CountUp from 'react-countup'
import { Button } from '../../../../common/index'
import SalesEditModal from './SalesEditModal'
import CalculationModal from './CalculationModal'
import BookletDetails from '../../BookletDetails'

interface AssociationSalesProps {
  associationId: string
  associationBooklets: Booklet[]
  selectedBooklet?: Booklet
  onBookletChange: React.Dispatch<React.SetStateAction<Booklet | undefined>>
  isMainAssociation: boolean
}

const AssociationSales: React.FC<AssociationSalesProps> = ({
  associationId,
  associationBooklets,
  selectedBooklet,
  onBookletChange,
  isMainAssociation,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);

  // State för completedOrder fördelning
  const [completedOrderDirectSales, setCompletedOrderDirectSales] = useState<number | null>(null);
  const [completedOrderResellerSales, setCompletedOrderResellerSales] = useState<number | null>(null);

  // Create options for react-select
  const bookletOptions = associationBooklets
    .filter((booklet: Booklet) => booklet.validFrom)
    .sort((a, b) => new Date(b.validFrom!).getTime() - new Date(a.validFrom!).getTime())
    .map((booklet: Booklet) => ({
      value: booklet,
      label: booklet.title
    }));

  // Query for association sales statistics filtered by booklet
  const { data: statsOrdersAssociation } = useQuery(statOrdersByAssociationQuery, {
    variables: {
      associationId,
      bookletId: selectedBooklet?._id || null,
      fromDate: null,
      toDate: null
    },
    skip: !selectedBooklet
  });

  // Query for booklet companies and coupons count
  const { data: countData } = useQuery(getBookletCompaniesCountQuery, {
    variables: { _id: selectedBooklet?._id },
    skip: !selectedBooklet
  });


  // Beräkna statistik från backend
  const webshopEarnings = statsOrdersAssociation?.orders?.webshopEarnings || 0;
  const generatedCompletedOrdersCount = statsOrdersAssociation?.orders?.generatedCompletedOrders?.length || 0;

  // Räkna antal butiker och kuponger
  const companyCount = countData?.booklet?.calculatedCompanies?.length || 0;
  const couponCount = countData?.booklet?.calculatedCompanies
    ? countData.booklet.calculatedCompanies.reduce((sum: number, company: any) => sum + (company.calculatedCoupons?.length || 0), 0)
    : 0;

  // Avgör om vi har värdebevis fördelning
  const hasCompletedOrderDistribution = completedOrderDirectSales !== null && completedOrderResellerSales !== null;

  // Kontrollera om denna association är huvudföreningen för häftet
  // const isMainAssociation = selectedBooklet?.mainAssociationId === associationId;

  // Beräkna värdebevis baserat på användarens angivna data (Sålda direkt/återförsäljare) enligt nya fält
  let generatedCompletedOrdersEarnings = 0;
  if (hasCompletedOrderDistribution && selectedBooklet) {
    const totalCommissionAmount = selectedBooklet.totalCommission || 0;
    const supportCommissionToMainAssociation = selectedBooklet.supportCommissionToMainAssociation || 0;

    if (isMainAssociation) {
      // Direktförsäljning via huvudföreningens shop: totalCommission (stödandel till underförening är inte relevant för värdebevis)
      const directEarnings = completedOrderDirectSales * totalCommissionAmount;
      // Återförsäljning: huvudföreningen får supportCommissionToMainAssociation
      const resellerEarnings = completedOrderResellerSales * supportCommissionToMainAssociation;
      generatedCompletedOrdersEarnings = Math.round(directEarnings + resellerEarnings);
    } else {
      // Vanlig förening: Får totalCommission minus stödandel till huvudförening för direktförsäljning
      const earningsPerBooklet = totalCommissionAmount - supportCommissionToMainAssociation;
      const directEarnings = completedOrderDirectSales * earningsPerBooklet;
      // Återförsäljning: vanlig förening får 0kr (stödet går till huvudföreningen)
      const resellerEarnings = completedOrderResellerSales * 0;
      generatedCompletedOrdersEarnings = Math.round(directEarnings + resellerEarnings);
    }
  }

  const totalEarnings = webshopEarnings + generatedCompletedOrdersEarnings;

  // Hantera sparande av generatedCompletedOrder fördelning
  const handleSaveGeneratedCompletedOrderDistribution = (directSales: number, resellerSales: number) => {
    setCompletedOrderDirectSales(directSales);
    setCompletedOrderResellerSales(resellerSales);
    setIsEditModalOpen(false);
  };

  // Reset värden när booklet ändras
  const handleBookletChange = (selectedOption: { value: Booklet }) => {
    onBookletChange(selectedOption.value);
    setCompletedOrderDirectSales(null);
    setCompletedOrderResellerSales(null);
  };


  return (
    <div className="container flex justify-center mb">
      <div className="box md">
        <div className="booklet-selector">
          <h3>Försäljning</h3>
          <div className="select-container">
            <Select
              value={bookletOptions.find(option => option.value === selectedBooklet)}
              onChange={handleBookletChange}
              options={bookletOptions}
              placeholder="Välj häfte"
              isSearchable={false}
              classNamePrefix="select"
            />
          </div>
        </div>

        <BookletDetails
          booklet={selectedBooklet}
          companyCount={companyCount}
          couponCount={couponCount}
        />

        <div className="completed-order-distribution flex items-center justify-between my">
          {!hasCompletedOrderDistribution ? (
            <>
              <p>Ange er försäljning av värdebevis<br />för att se all statistik.</p>
              <Button className='primary alt' onClick={() => setIsEditModalOpen(true)}>
                Ange
              </Button>
            </>
          ) : (
            <>
              <p>Statistiken baseras på din<br />angivna försäljningsdata.</p>
              <Button className='secondary' onClick={() => setIsEditModalOpen(true)}>
                Redigera
              </Button>
            </>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <div>
            <b>Totalt</b>
            <div className="sales-stats">
              <div className="stat-box lg">
                <div className="stat-label">Er förtjänst</div>
                <div className={`stat-value ${!hasCompletedOrderDistribution ? 'blurred' : ''}`}>
                  {/* @ts-ignore */}
                  <CountUp end={hasCompletedOrderDistribution ? totalEarnings : 0} />
                  <span>kr</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <b>Värdebevis</b>
            <div className="sales-stats">
              <div className="stat-box">
                <div className="stat-label">Er förtjänst</div>
                <div className={`stat-value ${!hasCompletedOrderDistribution ? 'blurred' : ''}`}>
                  {/* @ts-ignore */}
                  <CountUp end={generatedCompletedOrdersEarnings} />
                  <span>kr</span>
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">{!isMainAssociation ? 'Sålt' : 'Sålt av er eller återförsäljare'}</div>
                <div className={`stat-value ${!hasCompletedOrderDistribution ? 'blurred' : ''}`}>
                  {/* @ts-ignore */}
                  <CountUp end={hasCompletedOrderDistribution ? completedOrderDirectSales + completedOrderResellerSales : 0} />
                  <span>st</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <b>Webbshopar</b>
            <div className="sales-stats">
              <div className="stat-box">
                <div className="stat-label">Er förtjänst</div>
                <div className="stat-value">
                  {/* @ts-ignore */}
                  <CountUp end={webshopEarnings} />
                  <span>kr</span>
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">{!isMainAssociation ? 'Sålt totalt' : 'Sålt i er eller åf:s webbshopar'}</div>
                <div className="stat-value">
                  {/* @ts-ignore */}
                  <CountUp end={statsOrdersAssociation?.orders?.webshopOrders?.length || 0} />
                  <span>st</span>
                </div>
              </div>
            </div>
          </div>

          {isMainAssociation &&
            <Button className='primary md' onClick={() => setIsCalculationModalOpen(true)}>
              Hur räknar vi?
            </Button>
          }

          <small className='my'>Belopp anges alltid inkl. moms.</small>
        </div>
      </div>

      <SalesEditModal
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        onSave={handleSaveGeneratedCompletedOrderDistribution}
        initialDirectSales={completedOrderDirectSales || 0}
        initialResellerSales={completedOrderResellerSales || 0}
        maxTotalGeneratedCompletedOrders={generatedCompletedOrdersCount}
      />

      <CalculationModal
        isOpen={isCalculationModalOpen}
        closeModal={() => setIsCalculationModalOpen(false)}
        selectedBooklet={selectedBooklet}
        isMainAssociation={isMainAssociation}
      />
    </div >
  )
}

export default AssociationSales
